import React, { useState } from "react";
import WizardPageWrapper, { GeneralPageSummary } from "../WizardPageWrapper";
import Flex from "../../../../../../components/Flex";
import FileDropzone from "../FileDropzone";
import { Form, Input, Typography, Checkbox, Radio } from "antd";
import FormGroupItem from "../FormGroupItem";
import { CoverWizardForm, FormGroupTouched } from "../../CoverWizardModal";

type ImportantConsiderationsPageProps = {
  onNextButtonClick?: () => void;
  onBackButtonClick?: () => void;
  //the current form values are needed in order to have field values at mount (useWatch outputs current values at second rerender)
  initialFormValues?: CoverWizardForm;
  formGroupTouchedState: [FormGroupTouched, React.Dispatch<FormGroupTouched>];
};

const yesOrNoRadios = [
  { label: "Yes", value: "yes" },
  { label: "No", value: "no" },
];

const hasRadiusedCornersFieldName = "hasRadiusedCorners";
const cornerRadiusFieldName = "cornerRadius";

const hasCutCornersFieldName = "hasCutCorners";
const cutCornersRadiusFieldName = "cutCornersRadius";

export default function CornersPage({
  onNextButtonClick,
  onBackButtonClick,
  initialFormValues,
  formGroupTouchedState: [formGroupTouched, setFormGroupTouched],
}: ImportantConsiderationsPageProps) {
  const form = Form.useFormInstance<CoverWizardForm>();

  //---------radiused corners group state
  const hasRadiusedCornersAnswer =
    Form.useWatch(hasRadiusedCornersFieldName, { form, preserve: true }) ??
    initialFormValues?.hasRadiusedCorners;
  const cornerRadius =
    Form.useWatch(cornerRadiusFieldName, {
      form,
      preserve: true,
    }) ?? initialFormValues?.cornerRadius;
  const hasRadiusedCorners = hasRadiusedCornersAnswer === "yes";

  //---------cut corners group state
  const hasCutCornersAnswer =
    Form.useWatch(hasCutCornersFieldName, { form, preserve: true }) ??
    initialFormValues?.hasCutCorners;
  const cutCordersRadius =
    Form.useWatch(cutCornersRadiusFieldName, { form: form, preserve: true }) ??
    initialFormValues?.cutCornersRadius;
  const hasCutCorners = hasCutCornersAnswer === "yes";

  return (
    <WizardPageWrapper
      title="Corners"
      summary={<GeneralPageSummary />}
      onBackButtonClick={onBackButtonClick}
      onNextButtonClick={onNextButtonClick}
    >
      <Flex vertical gap="16px">
        <FormGroupItem
          label="Angled/Radiused Corners"
          closeButtonText={
            formGroupTouched.angledRadiusedCornersGroup ? "Save" : "Next"
          }
          onGroupClosed={() =>
            setFormGroupTouched({
              ...formGroupTouched,
              angledRadiusedCornersGroup: true,
            })
          }
          notExpandedDisplayValue={
            hasRadiusedCorners && !!cornerRadius
              ? cornerRadius
              : hasRadiusedCorners
              ? "Yes"
              : "None"
          }
          expandedOnMount={!formGroupTouched.angledRadiusedCornersGroup}
        >
          <Flex vertical gap="0.4rem">
            <Flex vertical gap="0.5rem">
              <Typography.Text>
                Does the pool have angled/radiused corners?
              </Typography.Text>
              <Radio.Group
                options={yesOrNoRadios}
                value={hasRadiusedCornersAnswer}
                onChange={(event) => {
                  const newValue = event.target.value;
                  if (newValue === "no") {
                    return form.setFieldsValue({
                      [hasRadiusedCornersFieldName]: newValue,
                      [cornerRadiusFieldName]: "",
                    });
                  }
                  form.setFieldValue(hasRadiusedCornersFieldName, newValue);
                }}
              />
            </Flex>
            {hasRadiusedCorners && (
              <Form.Item<CoverWizardForm>
                style={{ margin: "0" }}
                name={cornerRadiusFieldName}
                label="Dimensions/Radius"
              >
                <Input style={{ maxWidth: "282px", width: "100%" }} />
              </Form.Item>
            )}
          </Flex>
        </FormGroupItem>
        {formGroupTouched.angledRadiusedCornersGroup && (
          <FormGroupItem
            label="Cut Corners"
            closeButtonText="Save"
            notExpandedDisplayValue={
              hasCutCorners && !!cutCordersRadius
                ? cutCordersRadius
                : hasCutCorners
                ? "Yes"
                : "None"
            }
            onGroupClosed={() =>
              setFormGroupTouched({
                ...formGroupTouched,
                cutCornersGroup: true,
              })
            }
            expandedOnMount={!formGroupTouched.cutCornersGroup}
          >
            <Flex vertical gap="0.4rem">
              <Flex vertical gap="0.5rem">
                <Typography.Text>
                  Are cut corners required?
                  <br />
                  <Typography.Text italic>
                    4’ radius / 3’ angled walls must be cut. Smaller length or
                    radius will be squared unless noted otherwise.
                  </Typography.Text>
                </Typography.Text>
                <Radio.Group
                  options={yesOrNoRadios}
                  value={hasCutCornersAnswer}
                  onChange={(event) => {
                    const newValue = event.target.value;
                    if (newValue === "no") {
                      return form.setFieldsValue({
                        [hasCutCornersFieldName]: newValue,
                        [cutCornersRadiusFieldName]: "",
                      });
                    }
                    form.setFieldValue(hasCutCornersFieldName, newValue);
                  }}
                />
              </Flex>
              {hasCutCorners && (
                <Form.Item<CoverWizardForm>
                  style={{ margin: "0" }}
                  name={cutCornersRadiusFieldName}
                  label="Dimensions/Radius"
                >
                  <Input style={{ maxWidth: "282px", width: "100%" }} />
                </Form.Item>
              )}
            </Flex>
          </FormGroupItem>
        )}
      </Flex>
    </WizardPageWrapper>
  );
}
